import i18next from 'i18next'
import {makeAutoObservable, runInAction} from 'mobx'
import {inject, injectable} from 'inversify'
import {
    OnlinePaymentsPaymentModalStoreSymbol,
    OnlinePaymentsStoreSymbol,
    TOnlinePaymentsStore,
    TPaymentOperationStore
} from '@/pages/OnlinePayments'
import {chargePayment, refundPayment, refundAstropay} from '@/api'
import {TModalContainerStore} from '@/components/containers'
import {openErrorNotification, openSuccessNotification} from '@/utils'
import {PaymentMethodType} from '@/constants'
import {filterOperators} from '@/stores/transactions/OnlinePaymentsStore/OnlinePaymentsFilterButtonStore'
import {OnlineEcospendStoreSymbol, TEcospendStore} from '@/pages/components'
import {RefundTransactionType} from '@/api/transactions/models/RefundTransactionType'
import { refundGoCardlessPayment } from '../ecospend'

@injectable()
export class PaymentOperationStore implements TPaymentOperationStore {
    private readonly _paymentModal: TModalContainerStore
    private readonly _onlinePaymentsStore: TOnlinePaymentsStore
    private readonly _ecospendStore: TEcospendStore

    constructor(
        @inject(OnlinePaymentsPaymentModalStoreSymbol) paymentModal: TModalContainerStore,
        @inject(OnlinePaymentsStoreSymbol) onlinePaymentsStore: TOnlinePaymentsStore,
        @inject(OnlineEcospendStoreSymbol) ecospendStore: TEcospendStore
    ) {
        this._paymentModal = paymentModal
        this._onlinePaymentsStore = onlinePaymentsStore
        this._ecospendStore = ecospendStore

        makeAutoObservable(this)
    }

    public isLoading = false

    public loadCharge = async (paymentId: string, amount): Promise<void> => {
        if (this.isLoading) {
            return
        }

        runInAction(() => {
            this.isLoading = true
        })

        try {
            const {error} = await chargePayment(paymentId, amount)

            runInAction(() => {
                this.isLoading = false

                if (error) {
                    return openErrorNotification(error?.message)
                }

                this.onPaymentModalCancel()
                this.loadTransactionById(
                    this._onlinePaymentsStore.selectedTransaction.id,
                    filterOperators.in,
                    false
                )

                return openSuccessNotification(i18next.t('globals:success.paymentChargedSuccessfully'))
            })
        } catch (error) {
            return openErrorNotification(i18next.t('globals:errors.general'))
        }
    }

    public loadRefund = async (paymentId: string, data: RefundTransactionType): Promise<void> => {
        const {amount} = data

        if (this.isLoading) {
            return
        }
        runInAction(() => {
            this.isLoading = true
        })

        try {
            const {result, error} = await (() => {
                switch (this._onlinePaymentsStore.selectedTransaction.paymentMethod) {
                    case PaymentMethodType.astropay:
                        return refundAstropay(paymentId, Number(amount))
                    case PaymentMethodType.openbanking:
                        return refundGoCardlessPayment(paymentId, Number(amount))
                    default:
                        return refundPayment(paymentId, data)
                }
            })()


            await runInAction(async () => {
                if (error) {
                    return openErrorNotification(error?.message)
                }

                this.onPaymentModalCancel()
                this._onlinePaymentsStore.loadTransactions().then(() => {
                    this.loadTransactionById(result.id, filterOperators.in, true)
                })
                if (this._onlinePaymentsStore.selectedTransaction.balance === amount) {
                    return openSuccessNotification(
                        i18next.t('globals:success.paymentRefundedFullySuccessfully')
                    )
                }

                return openSuccessNotification(i18next.t('globals:success.paymentRefundedSuccessfully'))
            })
        } catch (error) {
            return openErrorNotification(i18next.t('globals:errors.general'))
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    public openEcospendRefundModal = (id: string, amount: number) => {
        this._ecospendStore.refund(id, amount)
        this._ecospendStore.loadBanks()
        this._paymentModal.onCancel()
    }

    loadTransactionById = async (
        transactionId: string,
        filterBy: string,
        loadByCurrentDate: boolean
    ) => {
        await this._onlinePaymentsStore.loadTransactionsById(
            transactionId,
            filterBy,
            loadByCurrentDate
        )

        await this._onlinePaymentsStore.refundRelativeTransactionsCheck()
    }

    public onPaymentModalCancel = () => {
        this._paymentModal.onCancel()
    }
}
